import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Belief } from "@/components/Belief";
import { useApp } from "@/lib/AppContext";
import { ApiError, type SubjectPageResponse } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";

/**
 * UI-16's **Subject** — the same page UI-10 draws, read rather than audited.
 *
 * The same call, the same beliefs, the same sections in the same order, and the same
 * superseded rule: a closed belief is struck through and says what replaced it, and the
 * ones whose window closed altogether stay behind the same disclosure they do in full
 * mode. Nothing is filtered out here that is shown there — this is a rendering, not a view.
 *
 * What is gone is everything that is *about the system* rather than about the subject: the
 * right rail's table of contents, page history, the wiki path and the git line; the
 * dossier link; the per-insight verdict badges and the links into the ledger. The
 * provenance itself stays, moved behind the quiet source label the shared belief renderer
 * draws in this variant — because a reader who does not want to know where a sentence came
 * from should not have to read it, and one who does should never be unable to find out.
 */
export function SimpleSubject() {
  const { slug = "" } = useParams();
  const { api } = useApp();

  const [answer, setAnswer] = useState<SubjectPageResponse | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSuperseded, setShowSuperseded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAnswer(await api.getSubject(slug));
      setProblem(null);
    } catch (failure) {
      setAnswer(null);
      setProblem(
        failure instanceof ApiError && failure.status === 404
          ? "There is nothing written about that."
          : "That page could not be read.",
      );
    } finally {
      setLoading(false);
    }
  }, [api, slug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => setShowSuperseded(false), [slug]);

  if (loading) {
    return (
      <p className="mx-auto max-w-[760px] px-[var(--space-8)] py-[var(--space-12)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading…
      </p>
    );
  }

  if (!answer) {
    return (
      <div className="mx-auto flex max-w-[760px] flex-col items-start gap-3 px-[var(--space-8)] py-[var(--space-12)]">
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
        <BackLink />
      </div>
    );
  }

  const { page } = answer;
  const meta = subjectKindMeta(page.kind);
  const Icon = meta.icon;
  const current = page.sections.reduce((total, section) => total + section.beliefs.length, 0);

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-[var(--space-8)] px-[var(--space-8)] py-[var(--space-12)]">
      <header className="flex flex-col gap-3">
        <BackLink />

        <div className="flex flex-wrap items-center gap-1.5 text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          <Icon size={12} strokeWidth={2.5} className="text-[var(--vl-virtual-soft)]" />
          <span>{meta.label}</span>
          <span aria-hidden className="opacity-50">
            ·
          </span>
          <span className="normal-case tracking-normal">
            {current} {current === 1 ? "belief" : "beliefs"} · updated {formatRelative(page.updatedAt)}
          </span>
        </div>

        <h1 className="text-[34px] font-extrabold leading-[1.15] tracking-[var(--tracking-snug)] text-[var(--text-primary)] [text-wrap:pretty]">
          {page.title}
        </h1>

        {(page.about ?? page.summary) && (
          <p className="text-[length:var(--text-lg)] leading-[1.65] text-[var(--text-secondary)] [text-wrap:pretty]">
            {page.about ?? page.summary}
          </p>
        )}
      </header>

      {current === 0 && page.superseded.length === 0 && (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Nothing is written about this yet.
        </p>
      )}

      {page.sections.map((section) => (
        <section key={section.anchor} id={section.anchor} className="flex flex-col gap-[var(--space-6)]">
          <h2 className="text-[length:var(--text-md)] font-bold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            {section.heading}
          </h2>
          {section.beliefs.map((belief) => (
            <Belief key={belief.id} belief={belief} slug={page.slug} variant="simple" />
          ))}
        </section>
      ))}

      {/* WORLD-1's closed windows, behind the same disclosure full mode puts them behind —
          same rule, plainer word. Simple mode hides machinery, not history. */}
      {page.superseded.length > 0 && (
        <div className="flex flex-col gap-[var(--space-6)]">
          <button
            type="button"
            onClick={() => setShowSuperseded((shown) => !shown)}
            aria-expanded={showSuperseded}
            className="inline-flex w-fit items-center rounded-[var(--radius-full)] border border-dashed border-[var(--border-default)] px-3 py-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]"
          >
            {showSuperseded ? "Hide what is no longer true" : "Show what is no longer true"}
          </button>

          {showSuperseded && (
            <section id="superseded" className="flex flex-col gap-[var(--space-6)] opacity-60">
              {page.superseded.map((belief) => (
                <Belief key={belief.id} belief={belief} slug={page.slug} variant="simple" />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/world-view"
      className="inline-flex w-fit items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]"
    >
      <ArrowLeft size={14} strokeWidth={2} />
      All subjects
    </Link>
  );
}
